/**
 * AlertRulesPage.tsx
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Main page for listing, filtering, and managing alert rules.
 *
 * Conformed to Spec 197 design blueprint — migrated off the hand-rolled
 * header/table/modal/buttons onto the canonical Tailwind+token UI kit
 * (PageLayout / Card / DataTable / Button / Badge / Modal / states). Behavior,
 * data fetching, server-side pagination, sorting, and state are unchanged.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, Bell, BellOff, Eye, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  useAlertRules,
  useToggleAlertRule,
  useDeleteAlertRule,
} from '../../../hooks/useAlertRules';
import type {
  AlertRuleView,
  AlertRuleFilters,
  PaginationParams,
  SortParams,
  Severity,
} from '@shared/types/alertRuleManagement';
import { AlertRuleFilters as FiltersPanel } from './components/AlertRuleFilters';
import { AlertRuleDetailPanel } from './components/AlertRuleDetailPanel';
import { AlertRuleForm } from './AlertRuleForm';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Select,
  Toggle,
  Badge,
  ErrorState,
  LoadingState,
  EmptyState,
  Modal,
  AppTooltip,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';

// =============================================================================
// Types
// =============================================================================

type SortField = SortParams['sortBy'];

// =============================================================================
// Helper Components
// =============================================================================

/**
 * Severity badge with color coding (kit <Badge>, token-driven).
 */
const SeverityBadge: React.FC<{ severity: Severity | null }> = ({ severity }) => {
  if (!severity) return <span className="text-sm text-[var(--color-text-secondary)]">—</span>;

  const variant: Record<Severity, 'error' | 'warning' | 'info'> = {
    Critical: 'error',
    Warning: 'warning',
    Info: 'info',
    High: 'warning',
    Medium: 'warning',
    Low: 'info',
  };

  return <Badge variant={variant[severity]}>{severity}</Badge>;
};

/**
 * Status badge for enabled/disabled (kit <Badge>).
 */
const StatusBadge: React.FC<{ enabled: boolean }> = ({ enabled }) =>
  enabled ? (
    <Badge variant="success" leftIcon={<Bell className="h-3 w-3" />}>
      Enabled
    </Badge>
  ) : (
    <Badge variant="secondary" leftIcon={<BellOff className="h-3 w-3" />}>
      Disabled
    </Badge>
  );

/**
 * Sortable column-header content (clicking a header toggles its sort).
 */
const SortHeader: React.FC<{
  field: SortField;
  label: string;
  currentSort: SortParams;
  onSort: (field: SortField) => void;
}> = ({ field, label, currentSort, onSort }) => {
  const isActive = currentSort.sortBy === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
    >
      {label}
      {isActive &&
        (currentSort.sortOrder === 'asc' ? (
          <ChevronUp className="h-3.5 w-3.5 text-[var(--color-accent)]" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--color-accent)]" />
        ))}
    </button>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const AlertRulesPage: React.FC = () => {
  // State
  const [filters, setFilters] = useState<AlertRuleFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({ page: 1, pageSize: 25 });
  const [sort, setSort] = useState<SortParams>({ sortBy: 'name', sortOrder: 'asc' });
  const [selectedRule, setSelectedRule] = useState<AlertRuleView | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRuleView | null>(null);
  const [deleteConfirmRule, setDeleteConfirmRule] = useState<AlertRuleView | null>(null);

  // Queries
  const { data, isLoading, isError, error, refetch, isFetching } = useAlertRules({
    filters,
    pagination,
    sort,
  });

  const toggleMutation = useToggleAlertRule();
  const deleteMutation = useDeleteAlertRule();

  // Computed values
  const rules: AlertRuleView[] = data?.data ?? [];
  const paginationInfo = data?.pagination;
  const totalPages = paginationInfo?.totalPages ?? 1;

  // Handlers
  const handleSort = useCallback((field: SortField) => {
    setSort((prev) => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleFilterChange = useCallback((newFilters: AlertRuleFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPagination({ page: 1, pageSize: newSize });
  }, []);

  const handleToggle = useCallback(
    async (rule: AlertRuleView) => {
      try {
        await toggleMutation.mutateAsync({ id: rule.id, enabled: !rule.enabled });
      } catch (err) {
        console.error('Failed to toggle rule:', err);
      }
    },
    [toggleMutation]
  );

  const handleViewDetails = useCallback((rule: AlertRuleView) => {
    setSelectedRule(rule);
    setIsDetailOpen(true);
  }, []);

  const handleEdit = useCallback((rule: AlertRuleView) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirmRule) return;
    try {
      await deleteMutation.mutateAsync(deleteConfirmRule.id);
      setDeleteConfirmRule(null);
    } catch (err) {
      console.error('Failed to delete rule:', err);
    }
  }, [deleteConfirmRule, deleteMutation]);

  const handleCreateNew = useCallback(() => {
    setEditingRule(null);
    setIsFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setIsFormOpen(false);
    setEditingRule(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setIsFormOpen(false);
    setEditingRule(null);
  }, []);

  // Check if any filters are active
  const hasFilters = useMemo(
    (): boolean =>
      filters.alertType !== undefined ||
      filters.severity !== undefined ||
      filters.enabled !== undefined ||
      Boolean(filters.search && filters.search.length > 0),
    [filters]
  );

  // Table columns (~100% width budget). The detail panel opens on row click, so
  // the action controls stopPropagation to avoid double-firing.
  const columns: DataTableColumn<AlertRuleView>[] = [
    {
      key: 'name',
      header: <SortHeader field="name" label="Name" currentSort={sort} onSort={handleSort} />,
      width: '24%',
      render: (rule) => (
        <span className="font-medium text-[var(--color-text-primary)]">{rule.name}</span>
      ),
    },
    {
      key: 'alertType',
      header: <SortHeader field="alertType" label="Alert Type" currentSort={sort} onSort={handleSort} />,
      width: '18%',
      render: (rule) => (
        <span className="text-[var(--color-text-secondary)]">
          {rule.alertType.replace(/([A-Z])/g, ' $1').trim()}
        </span>
      ),
    },
    {
      key: 'severityOverride',
      header: <SortHeader field="severityOverride" label="Severity" currentSort={sort} onSort={handleSort} />,
      width: '12%',
      render: (rule) => <SeverityBadge severity={rule.severityOverride} />,
    },
    {
      key: 'scope',
      header: 'Scope',
      width: '14%',
      render: (rule) => <span className="text-[var(--color-text-secondary)]">{rule.scope}</span>,
    },
    {
      key: 'enabled',
      header: <SortHeader field="enabled" label="Status" currentSort={sort} onSort={handleSort} />,
      width: '12%',
      render: (rule) => <StatusBadge enabled={rule.enabled} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '20%',
      render: (rule) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <AppTooltip title={rule.enabled ? 'Disable rule' : 'Enable rule'}>
            <Toggle
              checked={rule.enabled}
              onChange={() => handleToggle(rule)}
              disabled={toggleMutation.isPending}
              aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
            />
          </AppTooltip>
          <AppTooltip title="View details">
            <Button size="sm" variant="ghost" onClick={() => handleViewDetails(rule)} aria-label="View details">
              <Eye className="h-4 w-4" />
            </Button>
          </AppTooltip>
          <AppTooltip title="Edit rule">
            <Button size="sm" variant="ghost" onClick={() => handleEdit(rule)} aria-label="Edit rule">
              <Edit className="h-4 w-4" />
            </Button>
          </AppTooltip>
          <AppTooltip title="Delete rule">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDeleteConfirmRule(rule)}
              aria-label="Delete rule"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AppTooltip>
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      helpId="auto.alert-rules"
      title="Alert Rules"
      description="Manage alert rules for monitoring and notifications"
      icon={<Bell className="h-6 w-6" />}
      actions={
        <>
          <Button
            variant="outline"
            leftIcon={<RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={handleCreateNew}>
            Create Alert Rule
          </Button>
        </>
      }
    >
      {/* Filters (separate sub-component — left as-is per blueprint) */}
      <div className="mb-6">
        <FiltersPanel
          filters={filters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
          hasFilters={hasFilters}
        />
      </div>

      {/* Error State */}
      {isError && (
        <div className="mb-6">
          <ErrorState
            title="Failed to load alert rules"
            message={error?.message || 'Unknown error'}
            onRetry={() => refetch()}
          />
        </div>
      )}

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<Bell className="h-10 w-10" />}
            title={hasFilters ? 'No alert rules match your filters' : 'No alert rules configured'}
            action={
              hasFilters ? (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rules}
              rowKey={(rule) => rule.id}
              onRowClick={handleViewDetails}
              pageSize={pagination.pageSize}
              showPagination={false}
              minWidth="52rem"
            />

            {/* Server-side pagination — DataTable shows the full server page; the
                cross-page pager stays here (kit Buttons + Select). */}
            {paginationInfo && paginationInfo.total > 0 && (
              <div className="flex flex-col items-center justify-between gap-4 border-t border-[var(--color-border)] px-4 py-3 sm:flex-row">
                <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <span>
                    Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
                    {Math.min(pagination.page * pagination.pageSize, paginationInfo.total)} of{' '}
                    {paginationInfo.total} results
                  </span>
                  <Select
                    value={String(pagination.pageSize)}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    options={[
                      { value: '10', label: '10' },
                      { value: '25', label: '25' },
                      { value: '50', label: '50' },
                    ]}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="px-2 text-sm text-[var(--color-text-secondary)]">
                    Page {pagination.page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Detail Panel (separate sub-component, owns its own overlay) */}
      <AlertRuleDetailPanel
        rule={selectedRule}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onEdit={(rule) => {
          setIsDetailOpen(false);
          handleEdit(rule);
        }}
      />

      {/* Create/Edit Form Modal (separate sub-component, owns its own overlay) */}
      <AlertRuleForm
        editingRule={editingRule}
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation — hand-rolled modal migrated to the kit <Modal> */}
      <Modal
        isOpen={Boolean(deleteConfirmRule)}
        onClose={() => setDeleteConfirmRule(null)}
        title="Delete Alert Rule"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteConfirmRule(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-secondary)]">
          Are you sure you want to delete &quot;{deleteConfirmRule?.name}&quot;? This action cannot be
          undone. Historical alerts will be preserved.
        </p>
      </Modal>
    </PageLayout>
  );
};

export default AlertRulesPage;
